import type { Location, LocationHours, LocationId } from '@/lib/services/locations/locations.types'

import { InfoNotice, InfoNoticeAction } from '@/components/patterns/info-notice'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { ShoppingBasket, TriangleAlert, Truck } from 'lucide-react'

const hasOpenDay = (hours: Pick<LocationHours, 'is_open'>[] | null = []) =>
  Array.isArray(hours) && hours.some((day) => day?.is_open)

const hasDeliveryBaseServiceArea = (
  location: LocationFulfillmentSetupLocation | null | undefined
) =>
  (location?.delivery_postal_code_fees ?? []).some((fee) => fee?.postal_code?.trim()) ||
  (location?.delivery_city_fees ?? []).some((fee) => fee?.city?.trim())

const getFulfillmentStatus = ({
  isEnabled,
  isComplete,
}: {
  isEnabled: boolean
  isComplete: boolean
}): 'ready' | 'needs_setup' | 'off' => {
  if (isComplete) return 'ready'
  if (isEnabled) return 'needs_setup'
  return 'off'
}

// The setup UI reads only enabled flags, open-day flags and service-area labels.
// Full Location/hour/fee resources remain strict at their API owner.
export type LocationFulfillmentSetupLocation = Pick<Location, 'is_delivery' | 'is_pickup'> & {
  delivery_hours?: Pick<LocationHours, 'is_open'>[] | null
  open_hours?: Pick<LocationHours, 'is_open'>[] | null
  delivery_postal_code_fees?: Pick<
    NonNullable<Location['delivery_postal_code_fees']>[number],
    'postal_code'
  >[]
  delivery_city_fees?: Pick<NonNullable<Location['delivery_city_fees']>[number], 'city'>[]
}

export type LocationFulfillmentStatus = ReturnType<typeof getFulfillmentStatus>

export const getLocationFulfillmentSetupState = (
  location: LocationFulfillmentSetupLocation | null | undefined
) => {
  const isDeliveryEnabled = Boolean(location?.is_delivery)
  const isPickupEnabled = Boolean(location?.is_pickup)
  const isInitialFulfillmentSetup = !isDeliveryEnabled && !isPickupEnabled

  const isDeliveryComplete =
    isDeliveryEnabled &&
    hasOpenDay(location?.delivery_hours) &&
    hasDeliveryBaseServiceArea(location)
  const isPickupComplete = isPickupEnabled && hasOpenDay(location?.open_hours)
  const hasReadyFulfillment = isDeliveryComplete || isPickupComplete
  const hasIncompleteEnabledFulfillment =
    (isDeliveryEnabled && !isDeliveryComplete) || (isPickupEnabled && !isPickupComplete)

  return {
    isInitialFulfillmentSetup,
    hasReadyFulfillment,
    hasIncompleteEnabledFulfillment,
    delivery: {
      isEnabled: isDeliveryEnabled,
      isComplete: isDeliveryComplete,
      status: getFulfillmentStatus({
        isEnabled: isDeliveryEnabled,
        isComplete: isDeliveryComplete,
      }),
      shouldShow: isInitialFulfillmentSetup || (isDeliveryEnabled && !isDeliveryComplete),
      label: 'Configure delivery settings',
    },
    pickup: {
      isEnabled: isPickupEnabled,
      isComplete: isPickupComplete,
      status: getFulfillmentStatus({
        isEnabled: isPickupEnabled,
        isComplete: isPickupComplete,
      }),
      shouldShow: isInitialFulfillmentSetup || (isPickupEnabled && !isPickupComplete),
      label: 'Configure pickup settings',
    },
  }
}

const getSetupAlertCopy = (state: ReturnType<typeof getLocationFulfillmentSetupState>) => {
  if (state.isInitialFulfillmentSetup) {
    return {
      title: 'This location is not ready yet.',
      description:
        'Set up delivery or pickup before customers can use this location for online orders.',
    }
  }

  return {
    title: 'Finish this location setup.',
    description: state.hasReadyFulfillment
      ? 'One option is turned on but still needs setup before customers should see it online.'
      : 'Complete the enabled delivery or pickup settings before customers can use this location online.',
  }
}

export function LocationFulfillmentSetupActions({
  location,
  locationId,
}: {
  location?: LocationFulfillmentSetupLocation | null
  locationId?: LocationId | null
}) {
  const path = usePathHelper()

  if (!location || !locationId) {
    return null
  }

  const state = getLocationFulfillmentSetupState(location)
  const copy = getSetupAlertCopy(state)

  if (!state.delivery.shouldShow && !state.pickup.shouldShow) {
    return null
  }

  return (
    <InfoNotice
      tone="warning"
      title={copy.title}
      icon={<TriangleAlert />}
      action={
        <div className="flex flex-wrap gap-2">
          {state.delivery.shouldShow ? (
            <Can do="edit" on="delivery">
              <InfoNoticeAction {...path.deliveries.index().using({ locationId }).link}>
                <Truck className="size-4" />
                {state.delivery.label}
              </InfoNoticeAction>
            </Can>
          ) : null}
          {state.pickup.shouldShow ? (
            <Can do="edit" on="hours">
              <InfoNoticeAction {...path.hours.index().using({ locationId }).link}>
                <ShoppingBasket className="size-4" />
                {state.pickup.label}
              </InfoNoticeAction>
            </Can>
          ) : null}
        </div>
      }
    >
      {copy.description}
    </InfoNotice>
  )
}
